"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { setActiveTabAction } from "./actions";
import { assignLayoutAction, setLayoutForSlugAction } from "@/lib/layouts/actions";
import type { AreaSymbol, LayoutSymbol } from "@/lib/layouts/types";
import { saveAreaWindowsAction, setWindowsForSlugAction } from "@/lib/windows/actions";
import { saveAreaStateAction } from "@/lib/areas/actions";
import { toast } from "@/lib/notifications/toast";
import { EMPTY_WINDOWS_STATE, type UnpublishedWindow, type WindowsLayoutState } from "@/lib/windows/types";
import { getAdminMessages } from "@/lib/i18n/admin";
import {
  COOKIE_DEV_BANNER_HIDDEN,
  type ActiveTab,
  type AdminContextValue,
  type AdminNotification,
  type AdminUser,
  type PageType,
  type SaveScope,
} from "./types";

/**
 * Admin Context — globalny state dla top bara + edit mode + per-page state.
 *
 * **Per-page state (Faza G.9):** AdminProvider wymaga `currentSlug` + `currentPageType`
 * przekazane jako prop'y z page'a (server side). Mutators automatycznie operują
 * na current slug — komponent piszący nie musi się martwić routingiem.
 *
 * Initial values pochodzą z cookies czytanych server-side w PublicLayout.
 *
 * @see ../../.claude/CLAUDE.md sekcja "Komponenty admin"
 */

const AdminContext = createContext<AdminContextValue | null>(null);

export type AdminProviderProps = {
  user: AdminUser | null;
  scopes: string[];
  initialActiveTab: ActiveTab;
  /** Slug aktualnej strony — `"/"` lub `"/o-nas"` etc. Używany do persist'u Server Actions. */
  currentSlug: string;
  /** Typ aktualnej strony — decyduje pul layoutów w panelu. */
  currentPageType: PageType;
  /** Aktualne locale ('pl', 'en', 'de') — przekazywane do `saveAreaWindowsAction`
      jako `X-Imperial-Lang` header żeby backend zapisywał per-language. */
  locale: string;
  /** Backend `imp_cms_catalog.id` dla pageType="catalog" — używany do zapisu
      konfiguracji okien podstron (type=1). null = homepage (type=0). */
  currentIdCatalog: number | null;
  /** ID artykułu (`imp_cms_catalog.id` lub per-typ component) — non-null gdy
      bieżący URL ma article extras (np. `/blog/lorem-,42`). Decyduje czy panel
      admina pokaże radio `saveScope` z opcjami per-article overridów. NULL =
      catalog index lub homepage. */
  currentArticleId: number | null;
  /** Canonical request URI bez locale prefix (np. `/blog/lorem-42`) — zapisywany
      jako `request_uri` w `imp_cms_catalog_window.type=3` przy save'ie scope
      'this_url'. NULL gdy `currentArticleId` jest null. */
  currentRequestUri: string | null;
  /** Initial layout dla aktualnej strony (z cookie/default). */
  initialLayout: LayoutSymbol;
  /** Initial windows state dla aktualnej strony (z cookie/empty). */
  initialWindowsState: WindowsLayoutState;
  /** Meta okien NA AKTUALNEJ STRONIE — używane przez visitor view (lookup
      Map<id, meta>). Zawsze dostępne. Source: `publishedWindowsData` przez
      `adaptCmsWindow`. */
  pageWindows: UnpublishedWindow[];
  /** Drafty (okna NIE-przypisane do żadnej strony) — admin only, drag source
      w `LayoutAndWindowsPanel`. Visitor: pusty array. */
  unpublishedWindows: UnpublishedWindow[];
  /** Lista layoutów dostępnych dla domeny (fetched server-side w PublicLayout).
      Brak przypisań -> pusta lista -> panel pokaże pustą select listę. */
  availableLayouts: import("@/lib/api/cms-layouts").CmsLayout[];
  /** Mapa `id_window` -> Promise content'u (un-awaited, dla per-window
      streaming przez React `use()` + Suspense). Patrz `AdminContextValue.windowContents`. */
  windowContents: Map<
    number,
    Promise<import("@/lib/api/cms-window-content").WindowContentResponse<unknown> | null>
  >;
  /** Lista aktywnych języków bieżącej domeny — fetched server-side w PublicLayout.
      Konsumowane przez `AdminLocaleSwitch` w `AdminTopBar`. */
  domainLanguages: import("@/lib/api/domain-languages").DomainLanguage[];
  /** Source flagi z backend cascade — patrz `AdminContextValue.layoutSource`. */
  layoutSource: "catalog" | "template_default" | "fallback" | "invalid_for_template";
  windowsSource: "catalog" | "template_default" | "fallback" | "invalid_for_template";
  defaultTemplateExists: boolean;
  /** Active-template metadata (parity z backend meta). */
  activeTemplate: string | null;
  availableLayoutsCountHomepage: number;
  availableLayoutsCountSubpage: number;
  /** Symbol bieżącej domeny (np. "dev.v7.imperialcloud.io"). */
  domain: string;
  /** ID bieżącej domeny — dla dev banner deep-link'ów. */
  idDomain: number | null;
  /** ID grupy domen. */
  idDomainGroup: number | null;
  /** ID wiersza imp_cms_domain_template dla aktywnego szablonu. */
  idActiveDomainTemplate: number | null;
  /** Cascade-resolved lista `area_key` disabled w bieżącym scope. Hydratowane
      server-side z `meta.disabled_areas` (`IAPI_currentLayout` / `IAPI_windows`).
      Resetowane przy nawigacji (`currentSlug` change). */
  initialDisabledAreas: string[];
  /** Najwyższy tier konfiguracji obecny w bieżącym scope (z meta backendu).
      Używamy do inicjalizacji `saveScope` radio — admin widzi gdzie aktualnie
      żyje konfiguracja zamiast statycznego "this_url" dla każdego artykułu. */
  initialDominantSource: "specific_uri" | "all_articles" | "catalog" | null;
  /** Snapshot template_default dla preview mode (toggle "Pokaż układ domyślny").
      NULL gdy domena nie ma template_default lub fetch failed — toggle UI
      ukryte w panelu. */
  initialPreviewData: import("./types").PreviewData | null;
  /** Granular CMS permissions z `session.cmsPermissions`. NULL gdy brak admin
      session — visitor lub logged-in non-admin (oba treated like visitor). */
  initialCmsPermissions: import("@/lib/api/session").CmsPermissions | null;
  /** Per-katalogowe uprawnienia admina z `imp_user_group_privilege` (z backend
      `IAPI_pageBootstrap.data.admin_permissions`). NULL = visitor / non-admin
      / homepage / fetch failed. Frontend gate'uje przyciski katalogu
      (Konfiguruj / Usuń / Dodaj podstronę) per uprawnienie. */
  initialCatalogPermissions: import("@/lib/api/session").CatalogAdminPermissions | null;
  initialNotifications?: AdminNotification[];
  /** Initial wartość dla `devBannerHidden` — czytana server-side z cookie w
      PublicLayout. Przekazana jako prop żeby pierwszy paint już znał stan
      (brak flash'a po reload). */
  initialDevBannerHidden: boolean;
  adminPanelUrl: string;
  /** `true` gdy wydruk podstrony do PDF jest dostępny (z `cmsConfig` meta
      `pdf_export_available`) — gate'uje przycisk "Drukuj do PDF". */
  pdfExportAvailable: boolean;
  /** Aktualny język UI panelu admina — server-resolved z `IAPI_ssoCurrentSession`
      -> `$_SESSION['sa_lang']`. Read-only — zmiana języka tylko w classic IADMIN
      (React-CMS nie udostępnia switcher'a, synchronizuje się z PHP session). */
  adminLocale: string;
  /** CmsConfig — propagowane do AdminContext żeby admin UI (mini-mapa) mogło
      renderować ten sam logo source co visitor HDefault. */
  cmsConfig: import("@/lib/api/cms-config").CmsConfig | null;
  /** Trasa breadcrumb dla bieżącej strony — server-side built w PublicLayout
      z danych już dostępnych w bootstrap response. Konsumowane przez okno
      `w_path`. Dla homepage = `[home]`. */
  pageBreadcrumb: import("@/lib/cms/breadcrumb").BreadcrumbItem[];
  children: ReactNode;
};

export function AdminProvider({
  user,
  scopes,
  initialActiveTab,
  currentSlug,
  currentPageType,
  locale,
  currentIdCatalog,
  currentArticleId,
  currentRequestUri,
  initialLayout,
  initialWindowsState = EMPTY_WINDOWS_STATE,
  pageWindows,
  unpublishedWindows,
  availableLayouts,
  windowContents,
  domainLanguages,
  layoutSource,
  windowsSource,
  defaultTemplateExists,
  activeTemplate,
  availableLayoutsCountHomepage,
  availableLayoutsCountSubpage,
  domain,
  idDomain,
  idDomainGroup,
  idActiveDomainTemplate,
  initialDisabledAreas,
  initialDominantSource,
  initialPreviewData,
  initialCmsPermissions,
  initialCatalogPermissions,
  initialNotifications = [],
  initialDevBannerHidden,
  adminPanelUrl,
  pdfExportAvailable,
  adminLocale,
  cmsConfig,
  pageBreadcrumb,
  children,
}: AdminProviderProps) {
  const [activeTab, setActiveTabLocal] = useState<ActiveTab>(initialActiveTab);
  const [currentLayout, setCurrentLayoutLocal] = useState<LayoutSymbol>(initialLayout);
  const [windowsState, setWindowsStateLocal] = useState<WindowsLayoutState>(initialWindowsState);

  /* SaveScope — zakres zapisu okien/layoutu. Default zależy od backend
     `dominant_source` (najwyższy tier z dowolnym wpisem konfiguracji w
     bieżącym scope):
       - `specific_uri` -> 'this_url'
       - `all_articles` -> 'all_articles'
       - `catalog` -> 'catalog'
       - null (nic skonfigurowane) -> fallback per pageType:
         article -> 'this_url' (admin zazwyczaj dodaje per-URL override)
         catalog index / homepage -> 'catalog' (radio nieukazany)
     Cel: admin widzi gdzie żyje aktualna konfiguracja zamiast statycznego
     "this_url" dla każdego artykułu. Reset przy zmianie URL (`currentSlug`). */
  const resolveDefaultSaveScope = useCallback((): SaveScope => {
    if (currentArticleId === null) return "catalog"; //index/homepage — radio hidden
    if (initialDominantSource === "specific_uri") return "this_url";
    if (initialDominantSource === "all_articles") return "all_articles";
    if (initialDominantSource === "catalog") return "catalog";
    return "this_url"; //null — brak config, default dla article
  }, [currentArticleId, initialDominantSource]);

  const [saveScope, setSaveScopeLocal] = useState<SaveScope>(resolveDefaultSaveScope);
  const setSaveScope = useCallback((scope: SaveScope) => {
    /* Na catalog index radio nie istnieje — wymusza 'catalog'. No-op gdy
       caller mimo wszystko spróbuje zmienić. */
    if (currentArticleId === null) return;
    setSaveScopeLocal(scope);
  }, [currentArticleId]);
  const [draggedWindowId, setDraggedWindowId] = useState<number | null>(null);
  /* Selected window in "Nieopublikowane okna" panel — lifted z LayoutAndWindowsPanel
     żeby globalny dev banner (SelectedWindowDevBanner) mógł reagować na selekcję. */
  const [selectedWindowId, setSelectedWindowId] = useState<number | null>(null);
  /* Counter aktywnych async requestów — wrapper `withPendingRequest` inkrementuje
     przed, dekrementuje po zakończeniu (fin'aczy). Komponent `<AdminPendingBar>`
     subscribuje do tej wartości i renderuje sticky progress bar gdy >0. */
  const [pendingRequests, setPendingRequests] = useState(0);

  /* DisabledAreas — cascade-resolved lista `area_key` z imp_cms_catalog_area_state
     dla bieżącego URL'a. Initial wartość pochodzi z meta backend'u (równolegle
     z `initialLayout` / `initialWindowsState`), update lokalny przy toggle'u
     w panelu (`setAreaEnabled`). Reset przy slug change wraz z resztą per-page
     state (useEffect[currentSlug] poniżej). */
  const [disabledAreas, setDisabledAreasLocal] = useState<AreaSymbol[]>(
    () => initialDisabledAreas as AreaSymbol[],
  );

  /* Preview mode — toggle "Pokaż układ domyślny". Gdy true, context wystawia
     `previewData` zamiast committed (page render switches to template_default).
     Reset przy slug change (każda strona ma własny preview decision). */
  const [previewMode, setPreviewModeLocal] = useState<boolean>(false);
  const togglePreviewMode = useCallback(() => {
    if (initialPreviewData === null) return; //no default available
    setPreviewModeLocal((prev) => !prev);
  }, [initialPreviewData]);

  /* DevBannerHidden — czy AlignmentChecker (banner developera) jest schowany.
     Initial value czytana server-side z cookie `cms_dev_banner_hidden`
     (PublicLayout > readAdminCookies) i przekazywana jako prop -> pierwszy
     paint już zna stan, brak flash'a po reload.
     Toggle pisze cookie bezpośrednio przez `document.cookie` (bez Server
     Action / revalidatePath) — to lekka dev-only preferencja UI, nie warto
     re-revalidate'ować całej strony przy każdym kliknięciu. */
  const [devBannerHidden, setDevBannerHiddenLocal] = useState<boolean>(initialDevBannerHidden);
  const setDevBannerHidden = useCallback((hidden: boolean) => {
    setDevBannerHiddenLocal(hidden);
    if (typeof document !== "undefined") {
      const oneYear = 60 * 60 * 24 * 365;
      document.cookie = `${COOKIE_DEV_BANNER_HIDDEN}=${hidden ? "1" : "0"}; path=/; max-age=${oneYear}; samesite=lax`;
    }
  }, []);

  /* DevMockDropDownTree — czy `WDropDownMenu` ma wstrzykiwać przykładowe
     drzewo 5-poziomowe do testów wizualnych. Default `true` (dev domyślnie
     widzi mock), developer może wyłączyć przyciskiem w AdminTopBar.
     SSR-safe: server zawsze startuje z `true`, post-hydration czytany
     localStorage (klucz `cms_dev_mock_dropdown_tree`). */
  const [devMockDropDownTree, setDevMockDropDownTreeLocal] = useState<boolean>(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem("cms_dev_mock_dropdown_tree");
      if (stored === "0") setDevMockDropDownTreeLocal(false);
    } catch {
      /* SecurityError — localStorage nieprzystępne */
    }
  }, []);
  const setDevMockDropDownTree = useCallback((enabled: boolean) => {
    setDevMockDropDownTreeLocal(enabled);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem("cms_dev_mock_dropdown_tree", enabled ? "1" : "0");
      } catch {
        /* SecurityError — ignore */
      }
    }
  }, []);
  const [notifications, setNotifications] = useState<AdminNotification[]>(initialNotifications);
  const [, startTransition] = useTransition();

  /* Re-sync local state gdy nawigujemy między pages — slug change -> nowy
     initialLayout/initialWindowsState z cookie dla nowego slug'a.

     **CRITICAL:** dependency array MUSI być TYLKO `[currentSlug]`, NIE
     `[initialLayout, currentSlug]` ani `[initialWindowsState, currentSlug]`.
     Powód: `initialLayout` / `initialWindowsState` to props z Server Component
     i dostają nową referencję na KAŻDYM server re-renderze (Next.js revalidate,
     ISR refresh, focus revalidation). Trigger'owanie reset'a na zmianę
     referencji powoduje:
       - Przycisk "Zapisz" pojawia się ponownie sam (state cofa się do server'a
         podczas gdy user już zapisał lokalnie ale revalidate jeszcze nie wrócił
         z świeżymi danymi)
       - Cloning okien — chwilowa rozbieżność między context.windowsState
         (zresetowany do server'owej STAREJ wartości) a draftState w panelu
         (user'a zmiany), różne komponenty renderują z różnych źródeł
       - Race condition w `commitWindowsState` async save loop — każdy
         `saveAreaWindowsAction` -> `revalidatePath` -> wymuszony reset state
         w środku trwającej zmiany

     Sync POWINIEN dziać się TYLKO przy faktycznej nawigacji (slug zmieni
     się gdy user przejdzie z `/pl/A` na `/pl/B`). Wtedy mamy nowe initial*
     z cookie/SoT dla nowej strony — wartą pełnego reset'a. */
  useEffect(() => {
    setCurrentLayoutLocal(initialLayout);
    setWindowsStateLocal(initialWindowsState);
    setSaveScopeLocal(resolveDefaultSaveScope());
    setDisabledAreasLocal(initialDisabledAreas as AreaSymbol[]);
    setPreviewModeLocal(false); //reset preview decision per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlug]);

  /* Osobny resync TYLKO dla saveScope — fire'uje gdy zmienia się article
     context (katalog index ↔ article) lub dominantSource z backendu.

     **Dlaczego osobny effect:** `currentSlug` jest TEN SAM dla catalog index
     (`/pl/news`) i article (`/pl/news/article-,42`) bo `parseSlug` strip'uje
     extras z `pathSegments`. Effect powyżej z deps `[currentSlug]` NIE fire'uje
     się przy nawigacji catalog index -> article (slug bez zmian). Bez tego
     resync'a saveScope state z catalog index ("catalog") persistuje na article
     view, mimo że narrower radios (this_url, all_articles) są tam enabled —
     UI dezynchronizuje się z user intent.

     **Bezpieczeństwo deps:** `currentArticleId` jest URL-derived (parsuje
     `parseSlug.articles[0]?.id`) — zmienia się TYLKO przy realnej nawigacji,
     nie na server re-renderach. `initialDominantSource` to string z backend
     meta — value equality (`Object.is`), więc identyczna wartość nie triggeruje
     fire. Brak race condition z `initialLayout`/`initialWindowsState`
     (które re-referencują na każdym server re-render -> wykluczone z deps). */
  useEffect(() => {
    setSaveScopeLocal(resolveDefaultSaveScope());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentArticleId, initialDominantSource]);

  /**
   * Standardowy wrapper dla wszystkich async operacji admin panelu (Server Action,
   * fetch, multi-step pipelines). Inkrementuje `pendingRequests` przed wywołaniem,
   * dekrementuje w `finally` (więc również przy rzuconej exception). Komponent
   * `<AdminPendingBar>` renderuje sticky progress bar gdy `pendingRequests > 0`.
   *
   * **Auto-refresh po sukcesie:** każda operacja kończy się `router.refresh()`
   * żeby zsynchronizować client-side Router Cache (RSC payloads) z świeżymi
   * danymi z backendu. Server Actions już wywołują `revalidateCmsPageCache`
   * (updateTag + revalidatePath) co invaliduje SERVER cache, ale CLIENT cache
   * (Router Cache) trzyma stare RSC payloads aż do nawigacji albo router.refresh.
   * Bez tego refresh'a user widział stare ustawienia po save dopóki nie zrobił
   * Ctrl+Shift+R (hard reload omija wszystkie warstwy cache).
   *
   * Refresh nie czeka na completion — odpalamy go po `fn()` succeed; React
   * batchuje i nie blokuje UI. Pozostaje wewnątrz try żeby ZAWSZE odpalić
   * po sukcesie, ale przed dekrementacją pending count (visual feedback).
   *
   * Re-throws errors — caller nadal może łapać/handle'ować.
   */
  const router = useRouter();
  const withPendingRequest = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setPendingRequests((n) => n + 1);
    try {
      const result = await fn();
      /* Sync Router Cache po sukcesie. Idempotent — jeśli fn() nic nie
         zapisywał, refresh i tak pobierze świeży stan (no-op w praktyce). */
      router.refresh();
      return result;
    } finally {
      setPendingRequests((n) => Math.max(0, n - 1));
    }
  }, [router]);

  const hasScope = useCallback(
    (scope: string) => scopes.includes(scope) || scopes.includes("*"),
    [scopes],
  );

  /* Tłumaczenia panelu admina — resolved per-render z `adminLocale` (Server
     Component przekazuje aktualną wartość). Memoizacja niepotrzebna —
     `getAdminMessages` zwraca stabilną referencję per locale.

     **Read-only** — zmiana języka odbywa się WYŁĄCZNIE w classic IADMIN
     (przełącznik górnego paska). React-CMS czyta `$_SESSION['sa_lang']` przez
     SSO bridge przy każdym requeście; nawigacja po zmianie języka w IADMIN
     automatycznie pokaże nowe tłumaczenia (Next.js no-store fetch). */
  const t = getAdminMessages(adminLocale);

  const setActiveTab = useCallback((next: ActiveTab) => {
    setActiveTabLocal(next); //optimistic
    startTransition(async () => {
      await withPendingRequest(() => setActiveTabAction(next));
    });
  }, [withPendingRequest]);

  /**
   * Helper — wylicza `(type, idCatalog, requestUri)` dla zapisu w
   * `imp_cms_catalog_window` / `imp_cms_catalog_layouts` na podstawie bieżącej
   * strony + `saveScope`.
   *
   * Wartości:
   *   - Homepage -> type=0, idCatalog=null, requestUri=null
   *   - Catalog index -> type=1, idCatalog=X, requestUri=null
   *   - Article + saveScope='this_url'     -> type=3, idCatalog=X, requestUri=Y
   *   - Article + saveScope='all_articles' -> type=2, idCatalog=X, requestUri=null
   *   - Article + saveScope='catalog'      -> type=1, idCatalog=X, requestUri=null
   *
   * Returns `null` gdy nie da się zapisać (catalog bez resolved idCatalog —
   * mock-only). Caller spada wtedy do cookie fallback.
   */
  const resolveSaveTarget = useCallback((): {
    type: 0 | 1 | 2 | 3;
    idCatalog: number | null;
    requestUri: string | null;
  } | null => {
    let result: { type: 0 | 1 | 2 | 3; idCatalog: number | null; requestUri: string | null } | null;
    if (currentPageType === "homepage") {
      result = { type: 0, idCatalog: null, requestUri: null };
    } else if (currentIdCatalog === null) {
      result = null;
    } else if (currentArticleId === null) {
      //catalog index view
      result = { type: 1, idCatalog: currentIdCatalog, requestUri: null };
    } else {
      //article view — saveScope decyduje
      switch (saveScope) {
        case "this_url":
          result = { type: 3, idCatalog: currentIdCatalog, requestUri: currentRequestUri };
          break;
        case "all_articles":
          result = { type: 2, idCatalog: currentIdCatalog, requestUri: null };
          break;
        case "catalog":
          result = { type: 1, idCatalog: currentIdCatalog, requestUri: null };
          break;
      }
    }
    return result;
  }, [currentPageType, currentIdCatalog, currentArticleId, currentRequestUri, saveScope]);

  /* Layout setter — operuje na current slug. Backend SoT dla homepage +
     catalog pages z resolved id_catalog. Cookie fallback dla katalogów
     bez DB equivalent (mock-only). */
  const setLayoutForCurrentPage = useCallback(
    (next: LayoutSymbol) => {
      setCurrentLayoutLocal(next);
      startTransition(async () => {
        await withPendingRequest(async () => {
          const target = resolveSaveTarget();
          if (target !== null) {
            const result = await assignLayoutAction({
              layout: next,
              type: target.type,
              idCatalog: target.idCatalog,
              requestUri: target.requestUri,
              lang: locale,
              slug: currentSlug,
            });
            if (!result.ok) {
              toast.error(
                t.contextToasts.layoutSaveError(result.error ?? t.contextToasts.unknownError),
              );
            } else {
              toast.success(t.contextToasts.layoutSavedSuccess);
            }
          } else {
            /* Cookie fallback — catalog page bez resolved id_catalog. */
            await setLayoutForSlugAction(currentSlug, next);
          }
        });
      });
    },
    [currentSlug, locale, resolveSaveTarget, withPendingRequest, t],
  );

  /* Commit draft disabled areas — diff vs context.disabledAreas (committed),
     per zmieniony obszar fire Server Action, aktualizuje state optymistycznie.
     Wywoływane przez `LayoutMap.handleCommit` razem z `commitWindowsState`
     (przyciskie "Zatwierdź zmiany"). Catalog area odrzucane na backendzie. */
  const committedDisabledAreasRef = useRef<AreaSymbol[]>(disabledAreas);
  useEffect(() => {
    committedDisabledAreasRef.current = disabledAreas;
  }, [disabledAreas]);

  const commitAreaStateChanges = useCallback(
    async (draftDisabledAreas: AreaSymbol[]): Promise<void> => {
      const target = resolveSaveTarget();
      if (target === null) return; //mock-only catalog bez resolved id_catalog

      const prev = committedDisabledAreasRef.current;
      const prevSet = new Set(prev);
      const nextSet = new Set(draftDisabledAreas);

      /* Diff: area która zmieniła stan. */
      const changes: Array<{ areaKey: AreaSymbol; enabled: boolean }> = [];
      for (const area of nextSet) {
        if (!prevSet.has(area)) changes.push({ areaKey: area, enabled: false });
      }
      for (const area of prevSet) {
        if (!nextSet.has(area)) changes.push({ areaKey: area, enabled: true });
      }
      if (changes.length === 0) return; //noop — draft identyczny z committed

      /* Optymistyczne local update PRZED Server Action — page (WindowArea)
         od razu reflektuje committed (post-commit) state. */
      setDisabledAreasLocal(draftDisabledAreas);

      await withPendingRequest(async () => {
        const failures: string[] = [];
        for (const change of changes) {
          const result = await saveAreaStateAction({
            areaKey: change.areaKey,
            enabled: change.enabled,
            type: target.type,
            idCatalog: target.idCatalog,
            requestUri: target.requestUri,
            lang: locale,
            slug: currentSlug,
          });
          if (!result.ok) {
            failures.push(`${change.areaKey}: ${result.error ?? t.contextToasts.unknownError}`);
          }
        }
        if (failures.length > 0) {
          //rollback do prev committed (backend SoT)
          setDisabledAreasLocal(prev);
          toast.error(
            t.contextToasts.areaStateSaveError(failures.join("; ")),
          );
        } else {
          toast.success(
            changes.length === 1
              ? (changes[0].enabled
                  ? t.contextToasts.areaEnabledSuccess(changes[0].areaKey)
                  : t.contextToasts.areaDisabledSuccess(changes[0].areaKey))
              : t.contextToasts.areasSavedPlural(changes.length),
          );
        }
      });
    },
    [currentSlug, locale, resolveSaveTarget, withPendingRequest, t],
  );

  /* Windows state mutators — ref-based pattern (zob. reguła #18 w CLAUDE.md). */
  const stateRef = useRef(windowsState);
  useEffect(() => {
    stateRef.current = windowsState;
  }, [windowsState]);

  /**
   * Persistuje windows state — backend SoT (homepage) lub cookie (catalog, TODO).
   *
   * @param next             Nowy pełny windows state (optimistic update)
   * @param affectedAreas    Lista obszarów które się zmieniły — backend save
   *                         per area (bulk-replace). Pominięte = wszystkie areas.
   */
  const persistWindowsState = useCallback(
    (next: WindowsLayoutState, affectedAreas?: AreaSymbol[]) => {
      setWindowsStateLocal(next);
      stateRef.current = next;
      startTransition(async () => {
        await withPendingRequest(async () => {
          /* Backend SoT — wysyłamy zmiany do `imp_cms_catalog_window` per area.
             `resolveSaveTarget()` wyznacza (type, idCatalog, requestUri) na bazie
             pageType + currentArticleId + saveScope. Patrz JSDoc helper'a.
             Failed save -> toast.error; state lokalny pozostaje optimistycznie. */
          const target = resolveSaveTarget();

          if (target !== null) {
            const areasToSync: AreaSymbol[] = affectedAreas ?? (Object.keys(next.publishedByArea) as AreaSymbol[]);
            const failures: string[] = [];
            for (const area of areasToSync) {
              const windowIds = next.publishedByArea[area] ?? [];
              const result = await saveAreaWindowsAction({
                area,
                windowIds,
                type: target.type,
                idCatalog: target.idCatalog,
                requestUri: target.requestUri,
                lang: locale,
                slug: currentSlug,
              });
              if (!result.ok) {
                failures.push(`${area}: ${result.error ?? t.contextToasts.unknownError}`);
              }
            }
            if (failures.length > 0) {
              toast.error(t.contextToasts.areasSaveError(failures.join("; ")));
            } else if (areasToSync.length > 0) {
              /* Toast.success — krótkie potwierdzenie. Dla pojedynczego obszaru
                 nazwa go, dla wielu — liczba. Bar znika w międzyczasie, toast
                 daje wyraźną informację "tak, zapisałem". */
              const msg = areasToSync.length === 1
                ? t.contextToasts.areaSavedSingle(areasToSync[0])
                : t.contextToasts.areasSavedPlural(areasToSync.length);
              toast.success(msg);
            }
          } else {
            /* Cookie fallback — catalog page bez resolved id_catalog (mock-only
               katalog bez odpowiednika w `imp_cms_catalog`). Backend nigdy nie
               będzie miał danych dla takiej strony — działa jako lokalny mock. */
            await setWindowsForSlugAction(currentSlug, next);
          }
        });
      });
    },
    [currentSlug, locale, resolveSaveTarget, withPendingRequest, t],
  );

  const addWindowToArea = useCallback(
    (windowId: number, area: AreaSymbol, atIndex?: number) => {
      const prev = stateRef.current;
      /* Affected = każdy area który PRZED operacją zawierał `windowId` (zostanie
         z niego usunięty) + target area. */
      const affected = new Set<AreaSymbol>([area]);
      const cleaned: WindowsLayoutState["publishedByArea"] = {};
      for (const [a, ids] of Object.entries(prev.publishedByArea)) {
        const filtered = (ids ?? []).filter((id) => id !== windowId);
        cleaned[a as AreaSymbol] = filtered;
        if ((ids ?? []).length !== filtered.length) affected.add(a as AreaSymbol);
      }
      const targetIds = [...(cleaned[area] ?? [])];
      const insertAt =
        atIndex === undefined
          ? targetIds.length
          : Math.max(0, Math.min(atIndex, targetIds.length));
      targetIds.splice(insertAt, 0, windowId);
      persistWindowsState(
        { publishedByArea: { ...cleaned, [area]: targetIds } },
        Array.from(affected),
      );
    },
    [persistWindowsState],
  );

  const moveWindowInArea = useCallback(
    (windowId: number, area: AreaSymbol, toIndex: number) => {
      const prev = stateRef.current;
      const ids = (prev.publishedByArea[area] ?? []).filter((id) => id !== windowId);
      const clamped = Math.max(0, Math.min(toIndex, ids.length));
      ids.splice(clamped, 0, windowId);
      persistWindowsState(
        { publishedByArea: { ...prev.publishedByArea, [area]: ids } },
        [area],
      );
    },
    [persistWindowsState],
  );

  const moveWindowBetweenAreas = useCallback(
    (windowId: number, fromArea: AreaSymbol, toArea: AreaSymbol, toIndex?: number) => {
      const prev = stateRef.current;
      const fromIds = (prev.publishedByArea[fromArea] ?? []).filter((id) => id !== windowId);
      const toIds = (prev.publishedByArea[toArea] ?? []).filter((id) => id !== windowId);
      const insertAt =
        toIndex === undefined ? toIds.length : Math.max(0, Math.min(toIndex, toIds.length));
      toIds.splice(insertAt, 0, windowId);
      persistWindowsState(
        {
          publishedByArea: {
            ...prev.publishedByArea,
            [fromArea]: fromIds,
            [toArea]: toIds,
          },
        },
        [fromArea, toArea],
      );
    },
    [persistWindowsState],
  );

  const removeWindowFromArea = useCallback(
    (windowId: number, area: AreaSymbol) => {
      const prev = stateRef.current;
      const ids = (prev.publishedByArea[area] ?? []).filter((id) => id !== windowId);
      persistWindowsState(
        { publishedByArea: { ...prev.publishedByArea, [area]: ids } },
        [area],
      );
    },
    [persistWindowsState],
  );

  /* Bulk commit — diff'uje aktualny stan z `next`, oblicza affected areas i
     persistuje tylko te które się zmieniły. Używane przez draft mode w
     `LayoutAndWindowsPanel` — admin akumuluje zmiany lokalnie, klika
     "Zatwierdź zmiany" -> ten call zapisuje wszystko jednym przebiegiem.
     **Defensywnie** persistujemy też `currentLayout` — admin może edytować
     okna podstrony zanim explicit zmieni layout w dropdown. Bez tego zapisu
     visitor patrzy na default layout (LCatalogSingleColumn) bez sidebar'ów,
     a admin widzi LCatalogTwoColsLeft -> desync. Persist layoutu jest
     idempotentne (DELETE+INSERT na backend) — tylko gdy faktycznie różny
     od initialLayout (które startuje z bieżącego DB stanu). */
  const commitWindowsState = useCallback(
    async (next: WindowsLayoutState): Promise<void> => {
      const prev = stateRef.current;
      const allAreas = new Set<AreaSymbol>([
        ...(Object.keys(prev.publishedByArea) as AreaSymbol[]),
        ...(Object.keys(next.publishedByArea) as AreaSymbol[]),
      ]);
      const affected: AreaSymbol[] = [];
      for (const a of allAreas) {
        const prevIds = prev.publishedByArea[a] ?? [];
        const nextIds = next.publishedByArea[a] ?? [];
        if (
          prevIds.length !== nextIds.length ||
          prevIds.some((id, i) => id !== nextIds[i])
        ) {
          affected.push(a);
        }
      }
      if (affected.length === 0) {
        return; //noop — draft identyczny z committed
      }

      /* Forced layout persist PRZED windows save — gwarantuje że visitor widzi
         tę samą strukturę co admin (`currentLayout`). Bez tego subpage z brakiem
         layoutu w DB fallback'uje na DEFAULT_CATALOG_LAYOUT, ignorując sidebar'y
         które admin skonfigurował. Wrapper sequencingu: assign layout -> save
         windows w jednym `withPendingRequest` żeby unifikować ich state w DB
         przed revalidatePath.

         **Zwracamy Promise** żeby caller (np. LayoutMap z lock'iem panelu) mógł
         await'ować zakończenie save. Bez tego nie da się wiarygodnie wykryć
         "save zakończony" — `pendingRequests` przez `startTransition` jest
         deferred i nie widoczny w React render cycle. */
      const target = resolveSaveTarget();

      if (target !== null) {
        /* Optymistyczne local update najpierw — UI nie czeka na backend. */
        setWindowsStateLocal(next);
        stateRef.current = next;
        /* Bezpośrednie await (bez startTransition wrappera) — caller może
           zaczekać na zakończenie. startTransition robił async work non-urgent,
           co maskowało pendingRequests transitions i blokowało lock'i caller'a. */
        await withPendingRequest(async () => {
          const layoutResult = await assignLayoutAction({
            layout: currentLayout,
            type: target.type,
            idCatalog: target.idCatalog,
            requestUri: target.requestUri,
            lang: locale,
            slug: currentSlug,
          });
          if (!layoutResult.ok) {
            toast.error(
              t.contextToasts.layoutSaveErrorBeforeWindows(
                layoutResult.error ?? t.contextToasts.unknownError,
              ),
            );
            return; //nie zapisuj windows bez layoutu — będzie orphan
          }
          /* Po zapisaniu layoutu — zapisz okna (per affected area). */
          const failures: string[] = [];
          for (const area of affected) {
            const windowIds = next.publishedByArea[area] ?? [];
            const result = await saveAreaWindowsAction({
              area,
              windowIds,
              type: target.type,
              idCatalog: target.idCatalog,
              requestUri: target.requestUri,
              lang: locale,
              slug: currentSlug,
            });
            if (!result.ok) {
              failures.push(`${area}: ${result.error ?? t.contextToasts.unknownError}`);
            }
          }
          if (failures.length > 0) {
            toast.error(t.contextToasts.areasSaveError(failures.join("; ")));
          } else {
            toast.success(
              affected.length === 1
                ? t.contextToasts.areaSavedSingle(affected[0])
                : t.contextToasts.areasSavedPlural(affected.length),
            );
          }
        });
      } else {
        /* Cookie fallback — catalog bez resolved id_catalog (mock-only). */
        persistWindowsState(next, affected);
      }
    },
    [
      persistWindowsState,
      currentLayout,
      currentSlug,
      locale,
      resolveSaveTarget,
      withPendingRequest,
      t,
    ],
  );

  const markAsRead = useCallback((id: number) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    //TODO Faza F.5+: Server Action notifications/markRead/{id}
  }, []);

  /* Preview mode override layer — gdy `previewMode=true`, context wystawia
     `initialPreviewData` (snapshot template_default) zamiast committed state.
     WindowArea / WindowAreaView / LayoutMap czytają z context'u i automatycznie
     re-renderują z preview danymi. Edycja jest disabled (zob. `editLocked`
     w typach + warunki w handlerach). */
  const effectiveCurrentLayout = previewMode && initialPreviewData?.layout
    ? initialPreviewData.layout
    : currentLayout;
  const effectiveWindowsState = previewMode && initialPreviewData
    ? initialPreviewData.windowsState
    : windowsState;
  const effectivePageWindows = previewMode && initialPreviewData
    ? initialPreviewData.pageWindows
    : pageWindows;
  const effectiveDisabledAreas: AreaSymbol[] = previewMode ? [] : disabledAreas;

  const value: AdminContextValue = {
    user,
    scopes,
    hasScope,
    activeTab,
    setActiveTab,
    currentSlug,
    currentPageType,
    locale,
    currentIdCatalog,
    currentArticleId,
    currentRequestUri,
    saveScope,
    setSaveScope,
    currentLayout: effectiveCurrentLayout,
    setLayoutForCurrentPage,
    windowsState: effectiveWindowsState,
    addWindowToArea,
    moveWindowInArea,
    moveWindowBetweenAreas,
    removeWindowFromArea,
    commitWindowsState,
    draggedWindowId,
    setDraggedWindowId,
    selectedWindowId,
    setSelectedWindowId,
    devBannerHidden,
    setDevBannerHidden,
    devMockDropDownTree,
    setDevMockDropDownTree,
    pendingRequests,
    withPendingRequest,
    pageWindows: effectivePageWindows,
    unpublishedWindows,
    availableLayouts,
    windowContents,
    domainLanguages,
    layoutSource,
    windowsSource,
    defaultTemplateExists,
    activeTemplate,
    availableLayoutsCountHomepage,
    availableLayoutsCountSubpage,
    domain,
    idDomain,
    idDomainGroup,
    idActiveDomainTemplate,
    disabledAreas: effectiveDisabledAreas,
    commitAreaStateChanges,
    cmsPermissions: initialCmsPermissions,
    canModify: initialCmsPermissions?.modify === true,
    canDelete: initialCmsPermissions?.delete === true,
    catalogPermissions: initialCatalogPermissions,
    /* Per-catalog modify: wymaga GLOBALnego windows_manager.modify ORAZ per-catalog
       modify (jeśli `catalogPermissions` istnieje). NULL = nie ma katalogu
       w scope (np. homepage) -> fallback na sam canModify. */
    canModifyCatalog:
      initialCmsPermissions?.modify === true &&
      (initialCatalogPermissions === null || initialCatalogPermissions.modify === true),
    /* Per-catalog delete: GLOBAL `delete` + per-catalog `delete` + (NOT redactor
       LUB is_owner). Redaktor = user którego `has_full=false` AND `is_owner=false` —
       backend `catalogs.php::delete` blokuje takich nawet z `delete=1`. Reguła:
       `has_full || is_owner || (delete && !redactor)`. Upraszczamy: gdy
       `has_full=true` permissions.delete też jest true; gdy `is_owner=true`
       backend zezwala. Inaczej musi mieć `delete=true` na catalogu I nie być
       redaktorem (czyli `has_full=true`). Wynik: `is_owner || (has_full && delete)`.
       Plus zawsze globalny canDelete (windows_manager.delete). */
    canDeleteCatalog:
      initialCmsPermissions?.delete === true &&
      (initialCatalogPermissions === null ||
        initialCatalogPermissions.is_owner === true ||
        (initialCatalogPermissions.has_full === true && initialCatalogPermissions.delete === true)),
    previewMode,
    togglePreviewMode,
    previewData: initialPreviewData,
    notifications: {
      unreadCount: notifications.filter((n) => !n.read).length,
      recent: notifications,
      markAsRead,
    },
    adminPanelUrl,
    pdfExportAvailable,
    adminLocale,
    cmsConfig,
    pageBreadcrumb,
    t,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

/** Hook do AdminContext — throws if used outside provider. */
export function useAdmin(): AdminContextValue {
  const ctx = useContext(AdminContext);
  if (!ctx) {
    throw new Error("useAdmin() must be used inside <AdminProvider>");
  }
  return ctx;
}

/** Hook variant zwracający null gdy provider nieobecny — np. dla setup wizard pages. */
export function useAdminOptional(): AdminContextValue | null {
  return useContext(AdminContext);
}
